<template>
  <v-container fluid>
    <v-row>
      <v-col cols="3">
        <v-card outlined class="rounded-lg pa-1 fill-height">
          <v-card-title>Verfügbare Schichten</v-card-title>
          <v-list dense style="max-height: 70vh; overflow-y: auto">
            <v-list-item
              v-for="shift in availableShifts"
              :key="shift._id"
              class="draggable-shift mb-2"
              draggable="true"
              @dragstart="onDragStart(shift, $event)">
              <v-chip :color="shift.color" dark style="width: 100%; cursor: move">
                <v-icon left small>mdi-drag-vertical</v-icon>
                {{ shift.name }} ({{ shift.shortName }})
              </v-chip>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="9">
        <v-card outlined class="rounded-lg pa-2 fill-height">
          <v-card-title>Wochen-Vorlage: Standard-Schichtbedarf pro Tag</v-card-title>
          <v-card-text>Ziehen Sie die benötigten Schichten in die jeweilige Tages-Spalte.</v-card-text>
          <v-row dense v-if="!loading && weeklyTemplate.length === 7">
            <v-col v-for="day in weekDays" :key="day.value" class="day-column">
              <v-card
                outlined
                class="fill-height rounded-lg pa-2 day-drop-zone"
                @dragover.prevent="handleDragOver"
                @dragleave="handleDragLeave"
                @drop="onDrop(day.value, $event)">
                <div class="text-center font-weight-bold mb-2">
                  <div>{{ day.text }}</div>
                  <div class="text-caption grey--text">
                    M: {{ calculateDaySummaryCounts(day.value).morning }} | N: {{ calculateDaySummaryCounts(day.value).noon }} | A:
                    {{ calculateDaySummaryCounts(day.value).evening }}
                  </div>
                </div>
                <v-chip
                  v-for="(reqShift, index) in shiftsForDay[day.value]"
                  :key="`${day.value}-${reqShift.shiftTemplateId}-${index}`"
                  :color="reqShift.shiftTemplate ? reqShift.shiftTemplate.color : 'grey'"
                  dark
                  small
                  close
                  @click:close="removeShift(day.value, reqShift.shiftTemplateId, index)"
                  class="ma-1">
                  {{ reqShift.shiftTemplate ? reqShift.shiftTemplate.shortName : "Fehler" }}
                </v-chip>
                <p v-if="shiftsForDay[day.value] && shiftsForDay[day.value].length === 0" class="text-caption grey--text text-center mt-2">
                  Hier Schichten ablegen...
                </p>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-else class="fill-height" align="center" justify="center">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4 grey--text">Lade Vorlagendaten...</p>
          </v-row>
          <v-card-actions class="mt-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="saveTemplate" :loading="saving"> Wochen-Vorlage Speichern </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/api"
import Vue from "vue" // Importiere Vue für $set

export default {
  name: "WeeklyTemplatePlanner",
  props: {
    studioId: { type: String, default: null },
    availableShifts: { type: Array, required: true },
  },
  data() {
    return {
      loading: true, // Beginne mit loading: true
      saving: false,
      // Initialisiere leer, wird in loadTemplate gefüllt
      weeklyTemplate: [],
      draggedShift: null,
      weekDays: [
        { text: "Montag", value: 1 },
        { text: "Dienstag", value: 2 },
        { text: "Mittwoch", value: 3 },
        { text: "Donnerstag", value: 4 },
        { text: "Freitag", value: 5 },
        { text: "Samstag", value: 6 },
        { text: "Sonntag", value: 7 },
      ],
    }
  },
  computed: {
    // In methods: {}

    // Computed Property für die Anzeige, um Sortierung sicherzustellen
    shiftsForDay() {
      const dayMap = {}
      this.weekDays.forEach((day) => {
        const dayData = this.weeklyTemplate.find((d) => d.dayOfWeek === day.value)
        dayMap[day.value] = dayData
          ? [...dayData.requiredShifts].sort((a, b) => {
              const nameA = a.shiftTemplate?.shortName || ""
              const nameB = b.shiftTemplate?.shortName || ""
              return nameA.localeCompare(nameB)
            })
          : []
      })
      return dayMap
    },
  },
  methods: {
    calculateDaySummaryCounts(dayOfWeek) {
      const dayData = this.weeklyTemplate.find((d) => d.dayOfWeek === dayOfWeek)
      if (!dayData || !dayData.requiredShifts) {
        return { morning: 0, noon: 0, evening: 0 }
      }

      let morningCount = 0
      let noonCount = 0
      let eveningCount = 0

      // Annahme: Schichten sind bereits eindeutig (keine doppelten IDs pro Tag)
      // Wir zählen einfach jede *geplante* Schichtinstanz

      dayData.requiredShifts.forEach((reqShift) => {
        if (!reqShift.shiftTemplate || !reqShift.shiftTemplate.times || reqShift.shiftTemplate.times.length === 0) {
          return // Ungültige Schicht überspringen
        }

        // Finde die früheste Start- und späteste Endzeit der Schicht
        let earliestStart = null
        let latestEnd = null
        let hasWorkTime = false

        reqShift.shiftTemplate.times.forEach((time) => {
          if (time.type === "Arbeitszeit" && time.start && time.end) {
            hasWorkTime = true
            const startMoment = this.$moment(time.start, "HH:mm")
            const endMoment = this.$moment(time.end, "HH:mm")
            // Korrektur für über Mitternacht
            if (endMoment.isBefore(startMoment)) {
              endMoment.add(1, "day")
            }

            if (!earliestStart || startMoment.isBefore(earliestStart)) {
              earliestStart = startMoment
            }
            if (!latestEnd || endMoment.isAfter(latestEnd)) {
              latestEnd = endMoment
            }
          }
        })

        if (!hasWorkTime || !earliestStart || !latestEnd) return // Keine Arbeitszeit in dieser Schicht?

        // Definiere die Zeitgrenzen (können bei Bedarf angepasst werden)
        const morningThreshold = this.$moment("12:00", "HH:mm")
        const noonThresholdStart = this.$moment("12:00", "HH:mm")
        const noonThresholdEnd = this.$moment("18:00", "HH:mm")
        const eveningThreshold = this.$moment("18:00", "HH:mm")

        let countsInShift = { M: false, N: false, A: false }

        // Prüfe Morgen
        if (earliestStart.isBefore(morningThreshold)) {
          countsInShift.M = true
        }
        // Prüfe Nachmittag (überlappt mit 12-18 Uhr)
        if (latestEnd.isAfter(noonThresholdStart) && earliestStart.isBefore(noonThresholdEnd)) {
          countsInShift.N = true
        }
        // Prüfe Abend
        if (latestEnd.isAfter(eveningThreshold)) {
          countsInShift.A = true
        }

        // Zähle nur einmal pro Schichtinstanz für jeden Bereich
        if (countsInShift.M) morningCount++
        if (countsInShift.N) noonCount++
        if (countsInShift.A) eveningCount++
      })

      return { morning: morningCount, noon: noonCount, evening: eveningCount }
    },
    async loadTemplate() {
      if (!this.studioId) {
        console.warn("loadTemplate skipped: no studioId.")
        this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
        this.loading = false
        return
      }
      // Verhindere erneutes Laden, wenn schon aktiv
      if (this.loading && this.weeklyTemplate.length > 0) return

      this.loading = true
      console.log("Loading template for studio:", this.studioId)
      try {
        const templateDataFromApi = await api.shiftManagement.fetchWeeklyTemplate(this.studioId)
        console.log("Raw template data from API:", templateDataFromApi)

        if (!Array.isArray(templateDataFromApi)) {
          console.error("API did not return an array. Received:", templateDataFromApi)
          this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
        } else {
          const newTemplate = this.weekDays.map((dayDef) => {
            const existingDay = templateDataFromApi.find((t) => t.dayOfWeek === dayDef.value)
            const requiredShifts = (existingDay?.requiredShifts || [])
              .map((rs) => {
                // Nehme ID aus populate oder direkt
                const templateId = rs.shiftTemplateId?._id || rs.shiftTemplateId
                const fullShift = this.availableShifts.find((s) => s._id === templateId)
                if (!fullShift) {
                  console.warn(`Shift ID ${templateId} not found in availableShifts.`)
                  return null
                }
                return { shiftTemplateId: templateId, shiftTemplate: fullShift }
              })
              .filter((rs) => rs !== null)
            return { dayOfWeek: dayDef.value, requiredShifts: requiredShifts }
          })
          // Weise das komplett neue Array zu, um Reaktivität sicherzustellen
          this.weeklyTemplate = newTemplate
          console.log("Processed weeklyTemplate:", JSON.parse(JSON.stringify(this.weeklyTemplate)))
        }
      } catch (error) {
        this.$toast.error("Fehler beim Laden der Wochen-Vorlage.")
        console.error("Load Template Error:", error)
        this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
      } finally {
        this.loading = false
        console.log("Loading finished.")
      }
    },
    async saveTemplate() {
      if (!this.studioId) return
      this.saving = true
      try {
        const templateToSave = this.weeklyTemplate.map((dayData) => ({
          dayOfWeek: dayData.dayOfWeek,
          requiredShifts: dayData.requiredShifts.map((req) => ({ shiftTemplateId: req.shiftTemplateId })).filter((rs) => rs.shiftTemplateId),
        }))
        const savedTemplateData = await api.shiftManagement.saveWeeklyTemplate(this.studioId, templateToSave)
        this.$toast.success("Wochen-Vorlage gespeichert!")

        // *** WICHTIG: Verarbeite die Antwort, anstatt loadTemplate erneut aufzurufen ***
        const newTemplate = this.weekDays.map((dayDef) => {
          const existingDay = savedTemplateData.find((t) => t.dayOfWeek === dayDef.value)
          const requiredShifts = (existingDay?.requiredShifts || [])
            .map((rs) => {
              const templateId = rs.shiftTemplateId?._id || rs.shiftTemplateId
              const fullShift = this.availableShifts.find((s) => s._id === templateId)
              if (!fullShift) return null
              return { shiftTemplateId: templateId, shiftTemplate: fullShift }
            })
            .filter((rs) => rs !== null)
          return { dayOfWeek: dayDef.value, requiredShifts: requiredShifts }
        })
        this.weeklyTemplate = newTemplate // Aktualisiere mit der gespeicherten Version
      } catch (error) {
        this.$toast.error("Fehler beim Speichern der Wochen-Vorlage.")
        console.error("Save Template Error:", error)
      } finally {
        this.saving = false
      }
    },
    // getShiftsForDay wird jetzt durch computed property `shiftsForDay` ersetzt

    onDragStart(shift, event) {
      this.draggedShift = shift
      event.dataTransfer.setData("text/plain", shift._id)
      event.dataTransfer.effectAllowed = "copy"
      console.log("Drag Start:", shift._id)
    },
    // Methoden für visuellen Drop-Effekt (optional)
    handleDragOver(event) {
      event.currentTarget.style.backgroundColor = "rgba(0, 100, 255, 0.1)"
    },
    handleDragLeave(event) {
      event.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)"
    },
    onDrop(targetDayOfWeek, event) {
      event.preventDefault()
      event.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)" // Reset hover effect
      const shiftId = event.dataTransfer.getData("text/plain")
      console.log("Drop Event on Day:", targetDayOfWeek, "Shift ID:", shiftId)

      if (!shiftId) {
        /* Fehlerbehandlung */ return
      }
      const droppedShiftData = this.availableShifts.find((s) => s._id === shiftId)
      if (!droppedShiftData) {
        /* Fehlerbehandlung */ return
      }

      const dayDataIndex = this.weeklyTemplate.findIndex((d) => d.dayOfWeek === targetDayOfWeek)
      if (dayDataIndex > -1) {
        console.log("Adding shift to day:", targetDayOfWeek)
        const newShiftEntry = {
          shiftTemplateId: droppedShiftData._id,
          shiftTemplate: { ...droppedShiftData }, // Wichtig: Kopie!
        }
        // Füge hinzu und stelle Reaktivität sicher
        const updatedRequiredShifts = [...this.weeklyTemplate[dayDataIndex].requiredShifts, newShiftEntry]
        // Verwende Vue.$set, um das Array im Objekt reaktiv zu aktualisieren
        Vue.set(this.weeklyTemplate[dayDataIndex], "requiredShifts", updatedRequiredShifts)

        console.log("Updated shifts for day:", this.weeklyTemplate[dayDataIndex].requiredShifts)
      } else {
        console.error("Zieldaten für Tag nicht gefunden:", targetDayOfWeek)
      }
      this.draggedShift = null
    },
    removeShift(dayOfWeek, shiftTemplateIdToRemove, displayIndex) {
      const dayDataIndex = this.weeklyTemplate.findIndex((d) => d.dayOfWeek === dayOfWeek)
      if (dayDataIndex > -1) {
        // Finde *alle* Indizes dieser Schicht-ID im Original-Array
        const indicesToRemove = []
        this.weeklyTemplate[dayDataIndex].requiredShifts.forEach((shift, index) => {
          if (shift.shiftTemplateId === shiftTemplateIdToRemove) {
            indicesToRemove.push(index)
          }
        })

        // Finde den spezifischen Index basierend auf der sortierten Anzeige
        const sortedShifts = this.shiftsForDay[dayOfWeek]
        if (displayIndex < sortedShifts.length) {
          const shiftToRemove = sortedShifts[displayIndex]
          // Finde den tatsächlichen Index dieses spezifischen Elements im Original-Array
          // (wird knifflig, wenn gleiche Schicht mehrmals drin ist - wir nehmen den ersten Treffer)
          // Sicherer wäre, eine eindeutige ID pro hinzugefügter Schicht zu haben.
          // Workaround: Entferne den ersten Treffer mit dieser ID.
          const actualIndex = this.weeklyTemplate[dayDataIndex].requiredShifts.findIndex((rs) => rs.shiftTemplateId === shiftToRemove.shiftTemplateId)

          if (actualIndex > -1) {
            // Erstelle neues Array ohne das Element
            const updatedRequiredShifts = this.weeklyTemplate[dayDataIndex].requiredShifts.filter((_, idx) => idx !== actualIndex)
            Vue.set(this.weeklyTemplate[dayDataIndex], "requiredShifts", updatedRequiredShifts)
            console.log(`Removed shift ${shiftTemplateIdToRemove} at index ${actualIndex} for day ${dayOfWeek}`)
          } else {
            console.error("Could not find shift to remove.")
          }
        } else {
          console.error("Invalid display index for removal.")
        }
      }
    },
  },
  watch: {
    // Ruft loadTemplate nur auf, wenn sich studioId ÄNDERT und nicht null ist
    studioId: {
      async handler(newId, oldId) {
        console.log("Watcher: studioId changed", newId, oldId)
        // Nur auslösen, wenn sich die ID tatsächlich ändert UND eine neue ID vorhanden ist
        // Und initialLoadDone false ist (oder oldId null war -> erster gültiger Wert)
        if (newId && (newId !== oldId || !this.initialLoadDone)) {
          await this.loadTemplate()
        } else if (!newId) {
          this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
          this.loading = false
          this.initialLoadDone = false // Reset für nächsten Studio-Wechsel
        }
      },
      immediate: true, // Löst den Watcher sofort beim Erstellen aus
    },
  },
}
</script>

<style scoped>
.day-column {
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px !important;
}
.day-column:first-child {
  border-left: none;
}
.day-drop-zone {
  min-height: 60vh;
  background-color: rgba(255, 255, 255, 0.03);
  transition: background-color 0.2s ease-in-out;
}
/* Entferne :hover, stattdessen handleDragOver/Leave nutzen */
/*.day-drop-zone:hover {
     background-color: rgba(0, 100, 255, 0.1);
}*/
.draggable-shift .v-chip {
  width: 100%;
}
</style>
